'use client';

import { FC } from 'react';
import classNames from 'classnames';
import { AvatarPrimary } from '@nx-frontend/shared/ui/avatar';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { IconKey } from '@nx-frontend/shared/types/icon';

export interface EntityMenuItem {
  name: string;
  id: string;
  action?: (e: React.MouseEvent<HTMLDivElement>) => void;
  description?: string;
  avatarUrl?: string;
}

export interface EntityMenuAction {
  icon: IconKey;
  name: string;
  action: (e: React.MouseEvent<HTMLDivElement>) => void;
}

interface EntityMenuProps {
  items: EntityMenuItem[];
  actions?: EntityMenuAction[];
  createText?: string;
  onCreate?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const EntityMenu: FC<EntityMenuProps> = ({ items, actions, createText, onCreate }) => {
  return (
    <div className="text-sm flex overflow-hidden flex-col">
      <div className="border-b border-neutral-200">
        <EntityMenuItemComponent item={items[0]} isMain={true} />
        {actions && (
          <div className=" mb-2">
            {actions.map((action) => {
              return (
                <div
                  className="pl-9 flex flex-col items-start hover:bg-neutral-100 cursor-pointer"
                  key={action.name}
                  onClick={(e) => action.action?.(e)}
                >
                  <div className="px-4 flex gap-3 py-2 items-center">
                    <Icon icon={IconsMap[action.icon]} className="w-4 h-4 text-neutral-500" />
                    <span>{action.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="max-h-72 overflow-auto">
        {items.map((item, index) => {
          if (index === 0) {
            return <div key={0}></div>;
          }
          return <EntityMenuItemComponent key={item.id} item={item} isMain={false} />;
        })}
      </div>

      {createText && onCreate && (
        <div
          className="flex px-4 py-[10px] bg-zinc-50 cursor-pointer gap-2 items-center border-t border-neutral-200 rounded-b hover:bg-neutral-100"
          onClick={onCreate}
        >
          <div className="text-neutral-500">
            <AvatarPrimary size="md" icon={IconsMap.PLUS} name="" />
          </div>
          <span>{createText}</span>
        </div>
      )}
    </div>
  );
};

export const EntityMenuItemComponent = ({
  item,
  isMain,
  className,
  nameClass,
}: {
  item: EntityMenuItem;
  isMain?: boolean;
  className?: string;
  nameClass?: string;
}) => {
  return (
    <div
      key={item.id}
      className={classNames(
        'py-2 px-4 text-sm',
        {
          'cursor-pointer hover:bg-neutral-100': !isMain,
        },
        className
      )}
      onClick={(e) => item.action?.(e)}
    >
      <div className="flex gap-2 items-center">
        <AvatarPrimary size="md" name={item.name} avatarUrl={item.avatarUrl} />
        <div className="flex flex-col min-w-0">
          <div
            title={item.name}
            className={classNames(
              'whitespace-nowrap text-ellipsis overflow-hidden',
              {
                'font-semibold': isMain,
              },
              nameClass
            )}
          >
            {item.name}
          </div>
          {item.description && <div>{item.description}</div>}
        </div>
      </div>
    </div>
  );
};
